import "./App.css";
import { AuthContext } from "./context/auth";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import Home from "./components/+home";
import Login from "./components/+login/Login";
import PrivateRoute from "./PrivateRoute";
import { useEffect, useState } from "react";
import Get from "./helpers/ajax/get";
import InfoHome from "./components/+info/InfoHome";
import ResetPassword from "./components/+login/ResetPassword";
import ForgotPassword from "./components/+login/ForgotPassword";
import Join from "./components/+login/Join";
import Header from "./components/+sections/Header";
import Policies from "./components/+policies";
import Footer from "./components/+sections/Footer";
import Account from "./components/+account";
import About from "./components/+about";
import Request from "./components/+request";
import Contact from "./components/+contact";
import "react-tooltip/dist/react-tooltip.css";
import PrivacyPolicy from "./components/+privacy";
import SubmitterLogin from "./components/+submitter/SubmitterLogin";
import SubmitterReset from "./components/+submitter/SubmitterReset";
import SubmitterJobs from "./components/+submitter/SubmitterJobs";

function App() {
    const [authenticated, setAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [submitter, setSubmitter] = useState(null);
    const [colonyData, setColonyData] = useState(null);
    // Active colony for multi-unit users (persisted across sessions)
    const [activeColonyId, setActiveColonyId] = useState(
        () => localStorage.getItem("activeColonyId") || null
    );
    const navigate = useNavigate();

    document.title = "Home | Dependable";

    const fetchUser = async (colonyId = activeColonyId) => {
        try {
            const colonyParam = colonyId ? `&colony_id=${encodeURIComponent(colonyId)}` : "";
            var userData = await Get(`/api/user?_=${Date.now()}${colonyParam}`);
            var colonyDataGet = await Get(`/api/colony?_=${Date.now()}${colonyParam}`);
            setUser(userData);
            setAuthenticated(true);
            setColonyData(colonyDataGet);
        } catch (error) {
            console.log("not logged in");
        } finally {
            setLoading(false);
        }
    };

    // Switch the active colony (multi-unit users). Re-fetches all user data
    // scoped to the newly selected colony.
    const switchColony = async (colonyId) => {
        setActiveColonyId(colonyId);
        localStorage.setItem("activeColonyId", colonyId);
        setLoading(true);
        await fetchUser(colonyId);
    };

    const loadSubmitterData = async () => {
        try {
            var submitterData = await Get(`/api/submitters/me?_=${Date.now()}`);
            setSubmitter(submitterData);
            setAuthenticated(true);
        } finally {
            setLoading(false);
        }
    };

    const checkSession = async () => {
        try {
            await loadSubmitterData();
        } catch (error) {
            await fetchUser();
        }
    };

    const logout = () => {
        Get("/logout")
            .then(() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
                setUser(null);
                setSubmitter(null);
                setAuthenticated(false);
                navigate("/");
            })
            .catch(console.log);
    };

    useEffect(() => {
        checkSession();
        // eslint-disable-next-line
    }, []);

    return (
        <div className="text-center">
            {loading && (
                <div className="h-screen flex items-center justify-center">
                    <div className="ml-4 spinner-border animate-spin inline-block w-24 h-24 border-4 text-dependable rounded-full"></div>
                </div>
            )}

            {!loading && (
                <AuthContext.Provider value={{ authenticated, setAuthenticated, user, submitter, fetchUser, loadSubmitterData, checkSession, logout, colonyData, activeColonyId, switchColony }}>
                    {authenticated && !submitter && <Header />}
                    <Routes>
                        <Route exact path="/" element={authenticated ? (submitter ? <Navigate to={`/submitter/jobs`} /> : <Home />) : <InfoHome />} />
                        <Route exact path="/about" element={<About />} />
                        <Route exact path="/privacy-policy" element={<PrivacyPolicy />} />
                        <Route exact path="/contact" element={<Contact />} />
                        <Route exact path="/login" element={authenticated ? <Navigate to={`/`} /> : <Login />} />
                        <Route
                            exact
                            path="/reset/:reset_token"
                            element={authenticated ? <Navigate to={`/`} /> : <ResetPassword />}
                        />
                        <Route exact path="/join/:join_token" element={authenticated ? <Navigate to={`/`} /> : <Join />} />
                        <Route exact path="/forgot" element={authenticated ? <Navigate to={`/`} /> : <ForgotPassword />} />

                        <Route exact path="/submitter" element={authenticated ? <Navigate to={`/submitter/jobs`} /> : <SubmitterLogin />} />
                        <Route exact path="/submitter-reset/:token" element={authenticated ? <Navigate to={`/submitter/jobs`} /> : <SubmitterReset />} />
                        <Route exact path="/submitter/jobs" element={authenticated ? <SubmitterJobs /> : <Navigate to={`/submitter`} />} />

                        <Route path="/" element={<PrivateRoute />}>
                            <Route exact path="/policies" element={<Policies />} />
                            <Route exact path="/account" element={<Account />} />
                            <Route exact path="/winterize" element={<Request />} />
                            <Route exact path="/dewinterize" element={<Request />} />
                            <Route path="*" element={<Navigate to={`/`} />} />
                        </Route>
                    </Routes>
                    {authenticated && !submitter && <Footer />}
                </AuthContext.Provider>
            )}
        </div>
    );
}

export default App;